import { useState, useEffect } from 'react';

import type { ProjectAffiliationTeam } from './ProjectAffiliation';
import ProjectAffiliation from './ProjectAffiliation';
import { nuc } from '../../../../utils';
import { get } from '../../../../redux/api';


//  Helpers for getting URLs
const getTeamProjectURL = (locator: string = '') => `/api/projects/${nuc(locator)}/teams`;
const getTeamReleaseGroupURL = (releaseGroupId: string) => `/api/project_group/${releaseGroupId}/teams`;

type TeamProjectResponse = {
  totalCount: number;
  teams: ProjectAffiliationTeam[];
};

type ProjectAffiliationContainerProps = {
  projectLocator?: string;
  releaseGroupId?: number;
  labels?: string[];
};

/**
 * This is the "container component" (ie: holds all state/business) for the ProjectAffiliation component.
 *
 * @param {string} [projectLocator] - The project locator (if we are on a project summary page).
 * @param {number} [releaseGroupId] -The project group id (if we are on a release group summary page).
 */
const ProjectAffiliationContainer = ({
  projectLocator,
  releaseGroupId,
  labels,
}: ProjectAffiliationContainerProps) => {
  //  This informs what endpoint we hit. The responses are the same for both, so the child component doesn't care.
  const isProject: boolean = !!projectLocator;
  //  Storing various states.
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [teams, setTeams] = useState<ProjectAffiliationTeam[]>([]);

  //  After fetching, set everything in the state.
  const setFetchComplete = (response: TeamProjectResponse) => {
    setTotalCount(response.totalCount);
    setTeams(response.teams);
    setIsFetching(false);
  };

  //  Fetch the teams for the project/release group.
  const fetchTeams = async () => {
    setIsFetching(true);
    const response = await get<TeamProjectResponse>(isProject
      ? getTeamProjectURL(projectLocator)
      : getTeamReleaseGroupURL(`${releaseGroupId}`),
    );
    setFetchComplete(response.data);
  };

  //  Fetch on mount, or if the projectLocatorOrId changes.
  useEffect(() => {
    fetchTeams();
  }, [projectLocator, releaseGroupId]);

  return (
    <ProjectAffiliation
      labels={labels || []}
      isFetching={isFetching}
      isProject={isProject}
      teams={teams}
      totalCount={totalCount}
    />
  );
};

export default ProjectAffiliationContainer;
